import React, { useState, useEffect, useRef } from 'react';
import axios from '../../../../../config/axios';
import config from '../../../../../config';
import api from '../../../../../api';
import './AddVerifiedCallerIdModal.css';

const AddVerifiedCallerIdModal = ({ userId, onClose, onComplete }) => {
  const [defaultCountryCodeArr, setDefaultCountryCodeArr] = useState([]);
  const [remailCountryCodeArr, setRemailCountryCodeArr] = useState([]);
  const [country, setCountry] = useState('+1');

  const renderDropDownDefault = (arr, countryId = null) => {
    return arr.map((option, index) => (
      <option key={index} value={option.country_code} selected={countryId === option.country_code ? 'selected' : ''}>
        {option.country_name}
      </option>
    ));
  };

  const renderDropDownRemaining = (arr, countryId = null) => {
    return arr.map((option, index) => (
      <option key={index} value={option.country_code} selected={countryId === option.country_code ? 'selected' : ''}>
        {option.country_name}
      </option>
    ));
  };
  const [nickname, setNickname] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [extension, setExtension] = useState('');
  const [loading, setLoading] = useState(false);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    api.user.getUserInfo({}).then((res) => {
      const defaultArr = res.data?.user?.defaultCountryCodeArr || [];
      const remainingArr = res.data?.user?.remailCountryCodeArr || [];
      setDefaultCountryCodeArr(defaultArr);
      setRemailCountryCodeArr(remainingArr);
      if (defaultArr.length > 0) {
        setCountry(defaultArr[0].country_code);
      }
      setCountriesLoading(false);
    }).catch(() => {
      setDefaultCountryCodeArr([]);
      setRemailCountryCodeArr([]);
      setCountriesLoading(false);
    });

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleaned = phoneNumber.replace(/[^0-9]/g, '');
    if (!cleaned || cleaned.length < 10) {
      setError('Please enter a valid phone number with at least 10 digits.');
      return;
    }

    if (!nickname.trim()) {
      setError('Please enter a friendly name.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${config.apiBaseURL2}verified-caller-id/add`, {
        user_id: userId,
        country_code: country,
        phone_number: cleaned,
        extension: extension.replace(/[^0-9]/g, ''),
        verification_method: 'call',
        friendly_name: nickname.trim(),
      });

      if (response.data?.success) {
        onComplete(response.data.data || response.data);
      } else {
        setError(response.data?.message || 'Failed to add verified caller ID. Please try again.');
      }
    } catch (err) {
      if (!err.response) {
        setError('Unable to connect. Please check your internet connection.');
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        setError("You don't have permission to perform this action");
      } else {
        setError(err.response?.data?.message || err.message || 'Unable to add verified caller ID. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9+\-\s()]/g, '');
    setPhoneNumber(value);
    if (error) setError(null);
  };

  const handleNicknameChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    setNickname(value);
  };

  const handleExtensionChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setExtension(value);
  };

  return (
    <div className="voice-modal-overlay">
      <div className="voice-modal-content">
        <div className="voice-modal-header">
          <h3>Add Verified Caller ID</h3>
          <button className="voice-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form noValidate autoComplete="off" onSubmit={handleSubmit}>
          <div className="voice-modal-body">
            {error && (
              <div className="alert alert-danger" ref={errorRef}>
                {error}
              </div>
            )}

            <div className="info-box">
              <p>
                Add a phone number to verify as a caller ID. Verified Caller IDs help improve
                call answer rates by displaying your business name or phone number when you make calls.
              </p>
            </div>

            <div className="form-group">
              <label>Country<span className="required">*</span></label>
              <select
                className="form-input"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={loading || countriesLoading}
              >
                {countriesLoading ? (
                  <option value="">Loading countries...</option>
                ) : (
                  <>
                    {renderDropDownDefault(defaultCountryCodeArr, '+1')}
                    <optgroup label="Other countries">
                      {renderDropDownRemaining(remailCountryCodeArr, '')}
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            <div className="form-group">
              <label>Phone Number<span className="required">*</span></label>
              <input
                type="tel"
                className="form-input"
                placeholder="555 123 4567"
                value={phoneNumber}
                onChange={handlePhoneChange}
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Friendly Name<span className="required">*</span></label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Peter Parker"
                value={nickname}
                onChange={handleNicknameChange}
                disabled={loading}
              />
              <span className="field-hint">Enter a friendly name for this number</span>
            </div>

            <div className="form-group">
              <label>Extension</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. 123"
                value={extension}
                onChange={handleExtensionChange}
                disabled={loading}
              />
              <span className="field-hint">Optional extension number</span>
            </div>

            <div className="form-group">
              <label>Verification Method<span className="required">*</span></label>
              <div className="vci-radio-group">
                <label className="vci-radio-label vci-radio-checked">
                  <input
                    type="radio"
                    name="verificationMethod"
                    value="call"
                    checked
                    readOnly
                    disabled={loading}
                  />
                  <span className="vci-radio-text">
                    <strong>Call</strong>
                    <span className="vci-radio-desc">Twilio will call this number with a verification code</span>
                  </span>
                </label>
              </div>
              <span className="field-hint">Caller ID verification requires a phone call (Twilio does not support SMS for this).</span>
            </div>
          </div>

          <div className="voice-modal-footer">
            <button
              type="button"
              className="btn linear-gradient-cancel yr-cancelbtn mg-r"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn linear-gradient zy-save-btn ml-2"
              disabled={loading || !phoneNumber.trim() || !nickname.trim()}
            >
              {loading && (
                <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px' }} role="status" aria-hidden="true"></span>
              )}
              {loading ? 'Adding...' : 'Verify Number'}
            </button>
          </div>
        </form>

        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="ui active inline loader" style={{ marginBottom: '15px' }}></div>
              <p style={{ color: '#087ab6', fontSize: '14px', fontWeight: 500 }}>
                Adding Verified Caller ID...
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddVerifiedCallerIdModal;